import { reportError, reportWarning } from './reporter'

const reportUncaughtException = (message, url, lineNumber, columnNumber, error) =>
  reportError(message, {
    columnNumber,
    lineNumber,
    stack: error ? error.stack : undefined,
    url,
  })

const reportValidationError = (message, eventName, payload) =>
  reportError('event:validation:failed', {
    error: {
      message,
    },
    eventName,
    payload,
  })

const reportTrackerFailure = (error, eventName, payload) =>
  reportError('tracker:failed', {
    error: {
      message: error.message || error,
      readyState: error.readyState,
      status: error.status,
    },
    eventName,
    payload,
  })

const reportUnsuccessfulRequest = ({ body, error }) =>
  reportError('api:call:failed', { body, error })

const serializeError = (error) => ({
  message: error?.message || String(error),
  stack: error?.stack,
  status: error?.status,
})

const EXPECTED_CLIENT_ERROR_STATUSES = new Set([401, 403, 422, 429])

const reportAPIFailure = (error, payload) => {
  // Status Code from Authentication API specification:
  // https://confluence.internal.babbel.com/x/7JqRCw

  // Error data structure
  // error: {
  //   status: (int) 4XX,
  //   code: (string) 'mock_error',
  //   title: (string) 'Mock Error',
  //   detail: (string) 'Error is error'
  // }

  /*
   * The request was made but no response was received,
   * `error.request` is either an instance of XMLHttpRequest
   * or `error` is a generic error
   */

  if (error.status === undefined) {
    return reportWarning('api:call:network:failed', { body: payload, error: serializeError(error) })
  }

  if (error.status >= 500) {
    return reportUnsuccessfulRequest({ body: payload, error })
  }

  if (!EXPECTED_CLIENT_ERROR_STATUSES.has(error.status)) {
    return reportWarning('api:call:client:error', { body: payload, error })
  }

  return null
}

const reportUnhandledRejection = (reason) =>
  reportError('uncaught:promise:rejection', { error: serializeError(reason) })

const reportCrossOriginScriptError = () =>
  reportWarning('uncaught:error:cross-origin', {
    error: {
      message: 'Uncaught error from a cross-origin script (details masked by the browser)',
    },
  })

const reportNativeBridgeFailure = (method, payload) =>
  reportError('native:bridge:failed', {
    error: {
      message: `Neither iOS nor Android bridge accepted the "${method}" message`,
    },
    payload,
  })

const reportAuthFlowError = (step, error, payload) =>
  reportError('auth:flow:failed', {
    error: serializeError(error),
    payload,
    step,
  })

const reportCaptchaError = (message, payload) =>
  reportWarning('captcha:validation:failed', {
    error: {
      message,
    },
    payload,
  })

export {
  reportAPIFailure,
  reportAuthFlowError,
  reportCaptchaError,
  reportCrossOriginScriptError,
  reportNativeBridgeFailure,
  reportTrackerFailure,
  reportUncaughtException,
  reportUnhandledRejection,
  reportValidationError,
}
export { reportWarning } from './reporter'
